import React, { useRef } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { Prescription, Drug } from "common/models/types";
import Input from "components/atoms/Input";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import DrugSearchInput from "components/molecules/DrugSearchInput";
import { ConfirmManager } from "components/organisms/CommonUI/ConfirmPopup/ConfirmManager";
import { log, LogLevel } from "utils/LogUtil";

interface PrescriptionFormProps {
  prescription: Prescription;
  index: number;
  onUpdate: (prescription: Prescription, index: number) => void;
  onDelete: (index: number) => void;
}

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

// 최적화된 FormGroup 컴포넌트 생성
interface MemoizedFormGroupProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
}

const MemoizedFormGroup = React.memo(({ children, style }: MemoizedFormGroupProps) => {
  return <FormGroup style={style}>{children}</FormGroup>;
});

// 스로틀링 타이머 사용을 위한 RefObject
type ThrottleTimer = ReturnType<typeof setTimeout> | null;

const PrescriptionForm: React.FC<PrescriptionFormProps> = ({
  prescription,
  index,
  onUpdate,
  onDelete,
}) => {
  // 스로틀링 타이머 ref
  const throttleTimerRef = useRef<ThrottleTimer>(null);

  // 스로틀링을 적용한 상태 업데이트 함수
  const throttledUpdate = (updatedPrescription: Prescription) => {
    if (!throttleTimerRef.current) {
      // 실제 상태 업데이트
      onUpdate(updatedPrescription, index);

      // 스로틀링 타이머 설정
      throttleTimerRef.current = setTimeout(() => {
        throttleTimerRef.current = null;
      }, 100);
    }
  };

  return (
    <div
      style={{
        marginBottom: "16px",
        padding: "12px",
        backgroundColor: "white",
        borderRadius: "4px",
        border: `1px solid ${COLOR_SYSTEM.get("Gray")?.[200]}`,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "8px",
        }}
      >
        <FormGroup style={{ flex: 1, marginRight: "8px" }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
            <label style={{ fontWeight: "bold", marginRight: "8px" }}>의약품명</label>
            <span
              style={{
                fontSize: "0.8em",
                color: COLOR_SYSTEM.get("Gray")?.[500],
              }}
            >
              (검색: 의약품명, 보험코드번호, 표준코드)
            </span>
          </div>
          <DrugSearchInput
            value={prescription.medicationName || ""}
            onChange={(value) => {
              // 약품명만 변경되는 경우에는 업데이트를 간소화하여 포커스 유지
              const updatedPrescription = { ...prescription };
              updatedPrescription.medicationName = value;
              // 포커스 유지를 위해 state 업데이트 지연
              setTimeout(() => {
                onUpdate(updatedPrescription, index);
              }, 0);
            }}
            onSelect={(drug: Drug) => {
              // 이전 상태의 복사본 생성
              const updatedPrescription = { ...prescription };

              // 약품 정보 업데이트
              updatedPrescription.medicationName = drug.productName;
              updatedPrescription.itemCode = drug.itemCode;
              updatedPrescription.drug = drug;

              // 보험코드 설정 (standardCode 우선, 없으면 itemCode 사용)
              updatedPrescription.insuranceCode = drug.standardCode || drug.itemCode || "";

              // 디버깅 로그 추가
              log(LogLevel.VERBOSE, "선택된 약품 정보:", {
                medicationName: drug.productName,
                itemCode: drug.itemCode,
                standardCode: drug.standardCode,
                insuranceCode: updatedPrescription.insuranceCode,
                drug: drug,
              });

              // 상태 업데이트 (타이밍 이슈 방지를 위해 약간의 지연 적용)
              setTimeout(() => {
                log(LogLevel.VERBOSE, "약품 선택 후 처방전 업데이트 호출", updatedPrescription);
                onUpdate(updatedPrescription, index);
              }, 0);
            }}
            placeholder="의약품명, 보험코드번호, 표준코드로 검색"
          />
        </FormGroup>
        <Button
          buttonType="button"
          color="Negative"
          size="Small"
          type="Icon"
          variant="Ghost"
          icon="Trash"
          onClick={() => {
            ConfirmManager.show({
              title: `약품 삭제 (#${index + 1})`,
              body: `"${prescription.medicationName || "이름 없음"}" 약품을 삭제하시겠습니까?`,
              buttons: [
                <Button
                  key="confirm"
                  buttonType="button"
                  color="Negative"
                  size="Small"
                  type="Text"
                  variant="Contained"
                  onClick={() => {
                    log(
                      LogLevel.VERBOSE,
                      `의약품 삭제 요청: 인덱스=${index}, 이름=${prescription.medicationName}`
                    );
                    onDelete(index);
                    ConfirmManager.hide();
                  }}
                >
                  삭제
                </Button>,
                <Button
                  key="cancel"
                  buttonType="button"
                  color="Secondary"
                  size="Small"
                  type="Text"
                  variant="Outlined"
                  onClick={() => ConfirmManager.hide()}
                >
                  취소
                </Button>,
              ],
            });
          }}
          style={{ alignSelf: "flex-start", marginTop: "0px" }}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px" }}>
        <FormGroup>
          <Input
            label="코드번호"
            value={prescription.insuranceCode || ""}
            onChange={(e) => {
              const updatedPrescription = { ...prescription };
              updatedPrescription.insuranceCode = e.target.value;
              throttledUpdate(updatedPrescription);
            }}
            autoComplete="off"
          />
        </FormGroup>

        <FormGroup>
          <Input
            label="1회 투약량"
            type="text"
            value={
              prescription.dosagePerAdministration
                ? `${prescription.dosagePerAdministration}${prescription.dosageUnit || ""}`
                : ""
            }
            onChange={(e) => {
              const updatedPrescription = { ...prescription };
              const value = e.target.value;

              // 숫자와 단위 분리 (예: "1정" -> 숫자: 1, 단위: "정")
              const numericMatch = value.match(/^([\d.]+)(.*)$/);
              if (numericMatch) {
                // 숫자 부분
                updatedPrescription.dosagePerAdministration = parseFloat(numericMatch[1]) || 0;
                // 단위 부분
                updatedPrescription.dosageUnit = numericMatch[2]?.trim() || "";
              } else {
                // 숫자가 없는 경우
                updatedPrescription.dosagePerAdministration = 0;
                updatedPrescription.dosageUnit = value.trim();
              }

              throttledUpdate(updatedPrescription);
            }}
            placeholder="예: 1정, 5ml 등"
            autoComplete="off"
          />
        </FormGroup>

        <FormGroup>
          <Input
            label="1일 투약횟수"
            type="number"
            value={prescription.timesPerDay?.toString() || ""}
            onChange={(e) => {
              const updatedPrescription = { ...prescription };
              updatedPrescription.timesPerDay = parseInt(e.target.value) || 0;
              throttledUpdate(updatedPrescription);
            }}
            autoComplete="off"
          />
        </FormGroup>

        <FormGroup>
          <Input
            label="투약일수"
            type="number"
            value={prescription.daysOfAdministration?.toString() || "0"}
            onChange={(e) => {
              const updatedPrescription = { ...prescription };
              updatedPrescription.daysOfAdministration = parseInt(e.target.value) || 0;
              throttledUpdate(updatedPrescription);
            }}
            autoComplete="off"
          />
        </FormGroup>

        <MemoizedFormGroup style={{ gridColumn: "span 4" }}>
          <Input
            label="용법"
            value={prescription.usageInstruction || ""}
            onChange={(e) => {
              // 입력 값 저장
              const newValue = e.target.value;

              // 로컬 상태 업데이트
              const updatedPrescription = { ...prescription };
              updatedPrescription.usageInstruction = newValue;

              throttledUpdate(updatedPrescription);
            }}
            autoComplete="off"
          />
        </MemoizedFormGroup>
      </div>
    </div>
  );
};

export default React.memo(PrescriptionForm);
